import { useCallback } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useShallow } from "zustand/react/shallow";

import { ActivityTrigger, ActivityType } from "__graphql__/types";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { usePublishActivityStatusChangedMutation } from "flows/Activities/queries/deliveryCheckin/deliveryCheckin.generated";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { debounce } from "utils/debounce";

export function usePublishActivityStatusChanged(activityType: ActivityType) {
  const hubSlug = useHubSlug();

  const [employeeId, isIdentified] = useEmployeeStore(
    useShallow((state) => [state.badgeNo, state.isIdentified]),
  );
  const [publishActivityStatusChanged, { loading: isStatusUpdating, data }] =
    usePublishActivityStatusChangedMutation({
      onCompleted: () => {},
      onError: () => {},
    });
  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const publishActivityStatusStart = useCallback(
    debounce(() => {
      if (employeeId && hubSlug) {
        publishActivityStatusChanged({
          variables: {
            input: {
              activityType,
              trigger: ActivityTrigger.start,
            },
          },
          onError: (error) => {
            datadogRum.addError("publishActivityStatusChanged failed", {
              activityType,
              employeeId,
              trigger: ActivityTrigger.start,
              errorMessage: error.message,
            });
          },
        });
      }
    }, 200),
    [employeeId, isIdentified, hubSlug, activityType],
  );

  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const publishActivityStatusEnd = useCallback(
    debounce(() => {
      if (!data && !isStatusUpdating && employeeId && hubSlug) {
        publishActivityStatusChanged({
          variables: {
            input: {
              activityType,
              trigger: ActivityTrigger.end,
            },
          },
          onError: (error) => {
            datadogRum.addError("publishActivityStatusChanged failed", {
              activityType,
              employeeId,
              trigger: ActivityTrigger.end,
              errorMessage: error.message,
            });
          },
        });
      }
    }, 200),
    [employeeId, isIdentified, hubSlug, activityType],
  );

  return {
    publishActivityStatusStart,
    publishActivityStatusEnd,
  };
}
